import { Handle, Position, type NodeProps } from '@xyflow/react'
import { cn } from '@/lib/utils'
import { Play, Square, GitBranch, GitMerge, Workflow } from 'lucide-react'
import type { FlowNode } from '@/schemas/workflow-node'

function nodeLabel(data: unknown, fallback: string): string {
  const d = data as FlowNode | undefined
  return (d?.label && d.label.trim()) || fallback
}

export function StartNode({ data, selected }: NodeProps) {
  return (
    <div
      className={cn(
        'relative flex items-center justify-center gap-2.5 rounded-full border-2 px-6 py-2.5 min-w-[150px]',
        'bg-emerald-500/25 border-emerald-400/70 text-emerald-50',
        'shadow-[0_0_0_1px_rgba(16,185,129,0.25),0_8px_24px_-8px_rgba(16,185,129,0.55)]',
        selected && 'ring-2 ring-emerald-300 ring-offset-2 ring-offset-background'
      )}
    >
      <Play className="w-4 h-4 fill-emerald-200 text-emerald-200" />
      <span className="text-[13px] uppercase tracking-[0.12em] font-bold">
        {nodeLabel(data, 'Início')}
      </span>
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-emerald-300 !border-background !w-2.5 !h-2.5"
      />
    </div>
  )
}

export function EndNode({ data, selected }: NodeProps) {
  return (
    <div
      className={cn(
        'relative flex items-center justify-center gap-2.5 rounded-full border-2 px-6 py-2.5 min-w-[150px]',
        'bg-rose-500/25 border-rose-400/70 text-rose-50',
        'shadow-[0_0_0_1px_rgba(244,63,94,0.25),0_8px_24px_-8px_rgba(244,63,94,0.55)]',
        selected && 'ring-2 ring-rose-300 ring-offset-2 ring-offset-background'
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-rose-300 !border-background !w-2.5 !h-2.5"
      />
      <Square className="w-4 h-4 fill-rose-200 text-rose-200" />
      <span className="text-[13px] uppercase tracking-[0.12em] font-bold">
        {nodeLabel(data, 'Fim')}
      </span>
    </div>
  )
}

interface GenericNodeConfig {
  icon: typeof Workflow
  tint: string
  border: string
  accent: string
  fallback: string
}

function GenericNode({
  data,
  selected,
  config,
}: NodeProps & { config: GenericNodeConfig }) {
  const Icon = config.icon
  return (
    <div
      className={cn(
        'rounded-xl border bg-card px-4 py-3 min-w-[180px]',
        'border-border shadow-[0_4px_16px_-8px_rgba(0,0,0,0.6)]',
        selected && 'ring-2 ring-primary',
      )}
      style={{ borderLeft: `3px solid ${config.accent}` }}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-primary !border-background !w-2.5 !h-2.5"
      />
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex items-center justify-center w-7 h-7 rounded-md',
            config.tint,
            config.border,
            'border'
          )}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: config.accent }} />
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
            {config.fallback}
          </div>
          <div className="text-sm font-semibold text-foreground truncate">
            {nodeLabel(data, config.fallback)}
          </div>
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-primary !border-background !w-2.5 !h-2.5"
      />
    </div>
  )
}

export function TaskNode(props: NodeProps) {
  return (
    <GenericNode
      {...props}
      config={{
        icon: Workflow,
        tint: 'bg-indigo-500/10',
        border: 'border-indigo-500/30',
        accent: 'rgb(129 140 248)',
        fallback: 'Tarefa',
      }}
    />
  )
}

export function RouterNode(props: NodeProps) {
  return (
    <GenericNode
      {...props}
      config={{
        icon: GitBranch,
        tint: 'bg-amber-500/10',
        border: 'border-amber-500/30',
        accent: 'rgb(251 191 36)',
        fallback: 'Roteador',
      }}
    />
  )
}

export function JoinNode(props: NodeProps) {
  return (
    <GenericNode
      {...props}
      config={{
        icon: GitMerge,
        tint: 'bg-sky-500/10',
        border: 'border-sky-500/30',
        accent: 'rgb(56 189 248)',
        fallback: 'Unir',
      }}
    />
  )
}
